import React, { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { formatAmountForCopy, formatTxnAmount } from "../utils/currency";
import "./OfferCard.css";
import { roundPriceStep } from "../utils/roundPriceStep";
import { flyMoneyToBalance } from "../lib/flyToBalance";
import SetlistArt from "./SetlistArt";
import InstagramGlyph from "./InstagramGlyph";
import { normTier } from "../utils/orderTier";
import type { DemoAsk } from "../lib/demoRequest";
import { t as tr, formatLocale } from "../i18n";
import { T } from "../i18n/T";

interface OfferCardProps {
  _id: string;
  trackId: string;
  searchQuery: string;
  trackArtists: string[];
  amount?: number | null;
  avatarUrl: string;
  status: "pending" | "accepted" | "queued" | "countered";
  isPriority?: boolean;
  // ⭐ FAN BOOST — the fan spent points (★300) for visibility. Pixels only:
  // amount is unchanged, so this must NEVER render the amber BOOSTED
  // treatment (amber == more money is a DJ trust rule).
  fanBoost?: boolean;
  currency?: string;
  comment?: string;
  tier?: "standard" | "priority" | "fast" | null;
  createdAt?: string;
  clientNickname?: string | null;
  clientInstagram?: string | null;
  // The fan connected this handle with a DM to our Instagram (server
  // lib/orders/verifiedInstagram.js): the Instagram glyph in front of it says
  // so — no tick (founder, 2026-10-27: the Instagram icon means it's real).
  clientInstagramVerified?: boolean;
  stackCount?: number;
  contributors?: Array<{ nickname?: string | null; instagram?: string | null; instagramVerified?: boolean; createdAt?: string }>;
  // The amount BEFORE the first boost (server sets it on the first boost and
  // preserves it). Drives the struck "old → new" price on a boosted card.
  originalAmount?: number | null;
  // Number of fan-initiated bid increases on this order (0-3). ANY
  // boost (>= 1) gets the yellow BOOSTED treatment — the old "orange from the
  // 2nd boost, nothing on the 1st" split is gone (founder decision).
  increaseCount?: number;
  totalPaid?: number | null;
  // Countered — the base amount the artist asked for (never fee-inflated).
  counterAmount?: number | null;
  onAccept?: () => void;
  onDecline?: () => void;
  onPlay?: () => void;
  // This card's Play is still on its way to the server: the button is
  // disabled (attribute only — no style change, so the card's height can't
  // move). The parent ignores a second Play anyway (RequestsPage playStack).
  playBusy?: boolean;
  // Countered cards only — "Withdraw offer" (left swipe / desktop button).
  // The parent shows a confirm popup before anything happens server-side.
  onWithdrawCounter?: () => void;
  // DEMO practice request (new-DJ onboarding): purple DEMO sticker + a
  // "no real money" subline. Mechanics stay 100% identical to a real card —
  // that's the point. See src/lib/demoRequest.ts.
  isDemo?: boolean;
  /** Practice card 2 (founder, build 44): decline is the only gesture. */
  declineOnly?: boolean;
  /** Which card this is and which section it sits in — the page's glide
      between sections reads them (components/CardMoves). */
  moveKey?: string;
  moveList?: "pending" | "queued" | "countered";
  // A pending practice card's gesture (founder, 1.5.1 #6): the card peeks
  // toward that side every 3s until the talent first touches it.
  nudge?: DemoAsk;
  // A queued practice card's Play button pulses until it is tapped (founder,
  // 1.5.1 #6: people might just leave it there).
  nudgePlay?: boolean;
  // Setlist sale (Sell Playlists): 🎚️ tile instead of album art, title
  // "Tonight's Playlist", the gig night in the artists slot, amber
  // 🎚️ PLAYLIST badge in the amount row; fan + message ride the standard
  // 👤 client row. Accept goes through the parent's confirm popup; these
  // cards never reach the Queued list (delivery — not Play — is what
  // captures the money).
  isPlaylist?: boolean;
  // First 4 songs' album art (server setlistArt) — renders the 2×2 cover
  // mosaic in the art slot; absent → the 🎚️ tile stands in.
  trackImages?: string[] | null;
}

// How far the card opens. The action pill is exactly this wide and fills the
// whole revealed space as a solid panel (founder pick 6c-A, 1.5.1: "like
// iPhone Mail") — the old 180 left a big dead gap beside the shifted card.
const SWIPE_LIMIT = 130;

// The panels' icons (no emoji in product UI). Slot <0> of each label string.
const svgProps = {
  className: "action-icon",
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};
const ACCEPT_ICON = (
  <svg {...svgProps} strokeWidth={3.2}>
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </svg>
);
const DECLINE_ICON = (
  <svg {...svgProps} strokeWidth={3}>
    <path d="M6.5 6.5l11 11M17.5 6.5l-11 11" />
  </svg>
);
const WITHDRAW_ICON = (
  <svg {...svgProps} strokeWidth={3}>
    <path d="M9 14L4 9l5-5" />
    <path d="M20 20v-7a4 4 0 00-4-4H4" />
  </svg>
);

const formatElapsed = (iso?: string) => {
  if (!iso) return "";
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return tr("offerCard.justNow");
  if (mins < 60) return tr("offerCard.minutesAgo", { n: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return tr("offerCard.hoursAgo", { n: hrs });
  return tr("offerCard.daysAgo", { n: Math.floor(hrs / 24) });
};

export default function OfferCard({
  trackId,
  searchQuery,
  trackArtists,
  amount,
  avatarUrl,
  status,
  isPriority = false,
  fanBoost = false,
  currency,
  comment,
  tier,
  createdAt,
  clientNickname,
  clientInstagram,
  clientInstagramVerified = false,
  stackCount = 1,
  contributors = [],
  originalAmount,
  increaseCount = 0,
  totalPaid,
  counterAmount,
  onAccept,
  onDecline,
  onPlay,
  playBusy = false,
  onWithdrawCounter,
  isDemo = false,
  declineOnly = false,
  moveKey,
  moveList,
  nudge,
  nudgePlay = false,
  isPlaylist = false,
  trackImages = null,
}: OfferCardProps) {
  const isStacked = stackCount > 1;
  const [offset, setOffset] = useState(0);
  // The practice card's peek stops for good at the first touch (or drag).
  const [nudgeDone, setNudgeDone] = useState(false);
  const [showPriorityModal, setShowPriorityModal] = useState(false);
  const [showStackModal, setShowStackModal] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);
  const [, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  // Countered: withdraw (left swipe) is the ONLY gesture — no accept/decline
  // until the fan pays, at which point the server flips it back to a normal
  // pending card. (Founder rule.)
  const isCountered = status === "countered";
  const canSwipe =
    status === "pending" ||
    status === "accepted" ||
    status === "queued" ||
    (isCountered && !!onWithdrawCounter);
  const canSwipeOnlyDecline =
    status === "accepted" || status === "queued" || isCountered || declineOnly;

  const [isDesktop, setIsDesktop] = useState<boolean>(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(pointer: fine)").matches
      : false
  );

  useEffect(() => {
    const mq = window.matchMedia("(pointer: fine)");
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, []);

  const startX = useRef<number | null>(null);
  const baseOff = useRef(0);
  // A card left held open (swiped and not tapped) auto-recenters after 3s so
  // no card ever gets stuck part-way — that parked state is where layout
  // glitches hide (founder request, 1.1(16)).
  const recenterTimer = useRef<number | null>(null);
  const clearRecenter = () => {
    if (recenterTimer.current != null) {
      window.clearTimeout(recenterTimer.current);
      recenterTimer.current = null;
    }
  };
  const scheduleRecenter = () => {
    clearRecenter();
    recenterTimer.current = window.setTimeout(() => {
      recenterTimer.current = null;
      setOffset(0);
    }, 3000);
  };
  useEffect(() => clearRecenter, []); // clear on unmount
  const isPending = status === "pending";

  const safeAmount = Number.isFinite(Number(amount)) ? Number(amount) : 0;

  const tierVal = normTier(tier);

  // The server stores the CURRENT bid amount directly on order.amount (every
  // boost rewrites it), so the card displays amount as-is. Setlist prices
  // live on their own .99 grid — never snap them onto the request grid
  // (a $4.99 sale must never display as $5.00).
  const baseRounded = isPlaylist ? safeAmount : roundPriceStep(safeAmount, currency);
  const totalRounded = baseRounded;

  // Founder decision — ONE "boosted" treatment: yellow edge +
  // a ⚡ BOOSTED badge in the amount row, for a Fast Pass (tier fast) AND for
  // any boost (increaseCount >= 1). Before, a single boost was invisible on
  // the card and 2+ boosts went orange.
  const boostCount = increaseCount ?? 0;
  const isBoosted = !!isPriority || boostCount >= 1;
  // The struck "old → new" price needs a REAL earlier amount on this very
  // request: originalAmount (set on the first boost). A plain Fast Pass is one
  // payment — nothing to strike — so it shows the badge + amount only. Stacked
  // cards show the COMBINED total, so a lead-order original would mislead;
  // the stack popup carries the breakdown instead.
  const boostFrom =
    isBoosted && !isStacked && typeof originalAmount === "number" && originalAmount > 0
      ? roundPriceStep(originalAmount, currency)
      : null;
  const showBoostFrom = boostFrom != null && boostFrom !== totalRounded;

  // Every amount on the card goes through the one row formatter: the
  // currency's natural precision (2 decimals; 0 for HUF/JPY), symbol on the
  // right side for suffix currencies, thousands separators — so green and
  // yellow amounts can never disagree (founder: "$30.00" always).
  const fmt = (n: number) => formatTxnAmount(n, currency);
  // The $ (Play) button is a control, not an amount row: it keeps its short
  // form ("$10", "$12.50") — same look as before, now currency-correct too.
  const playLabel = formatAmountForCopy(totalRounded, currency);

  const getX = (e: React.TouchEvent | React.MouseEvent) =>
    "touches" in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;

  const handleStart = (e: React.TouchEvent | React.MouseEvent) => {
    if (!canSwipe) return;
    // Desktop uses inline buttons — don't initiate swipe on mouse drag.
    if (isDesktop && !("touches" in e)) return;
    if (nudge) setNudgeDone(true);
    clearRecenter(); // a new touch cancels any pending auto-recenter
    startX.current = getX(e);
    baseOff.current = offset;
  };

  const handleMove = (e: React.TouchEvent | React.MouseEvent) => {
    if (!canSwipe || startX.current == null) return;
    if (isDesktop && !("touches" in e)) return;
    const delta = getX(e) - startX.current;
    const raw = baseOff.current + delta;
    const clamped = canSwipeOnlyDecline
      ? Math.max(-SWIPE_LIMIT, Math.min(0, raw))
      : Math.max(-SWIPE_LIMIT, Math.min(SWIPE_LIMIT, raw));
    setOffset(clamped);
  };

  const TRIGGER = 80;

  const triggerAccept = () => {
    clearRecenter();
    setOffset(0);
    onAccept?.();
  };
  const triggerDecline = () => {
    clearRecenter();
    setOffset(0);
    onDecline?.();
  };
  const triggerWithdraw = () => {
    clearRecenter();
    setOffset(0);
    onWithdrawCounter?.();
  };

  const handleEnd = () => {
    if (!canSwipe) return;
    let final = 0;
    if (!canSwipeOnlyDecline && offset >= TRIGGER) {
      final = SWIPE_LIMIT;
    } else if (offset <= -TRIGGER) {
      final = -SWIPE_LIMIT;
    }
    setOffset(final);
    startX.current = null;
    // Held open without a tap → glide back to center after 3s.
    if (final !== 0) scheduleRecenter();
    else clearRecenter();
  };

  // Playlist card: same four-row skeleton as a song card so every card in
  // the list measures identical (contract §1). The artists slot carries the
  // night ("Saturday night", from the sale's own timestamp); the fan +
  // message ride the STANDARD 👤 client row below, tap-through and all.
  const playlistNight = (() => {
    if (!isPlaylist || !createdAt) return tr("offerCard.tonight");
    const d = new Date(createdAt);
    if (isNaN(d.getTime())) return tr("offerCard.tonight");
    // Gig-night semantics: a 1am sale belongs to the evening's night, so the
    // weekday reads from 6 hours back.
    const night = new Date(d.getTime() - 6 * 60 * 60 * 1000);
    return tr("offerCard.weekdayNight", { weekday: night.toLocaleDateString(formatLocale(), { weekday: "long" }) });
  })();

  const authorFull = isPlaylist
    ? playlistNight
    : trackArtists && trackArtists.length > 0
      ? trackArtists.join(", ")
      : tr("offerCard.unknownArtist");
  const titleFull = isPlaylist ? tr("offerCard.tonightSPlaylist") : searchQuery || "";

  const authorDisplay = authorFull;
  const titleDisplay = titleFull;

  // Countered — Option A price morph (founder pick): the amount line
  // itself says "COUNTER SENT, old becomes new" in one row, so the card stays
  // exactly normal height. Counter amounts use the same row
  // formatter as every other amount (always "$20.00", never "$20").
  const counterRounded = roundPriceStep(Number(counterAmount) || 0, currency);

  // Card edge — ONE rule (founder decision): every card has the same
  // contour + glow, only the colour changes. Green = standard, yellow =
  // boosted / Fast Pass / counter sent. Stacks no longer change the edge —
  // the 🔥 ×N sticker carries that on its own.
  const edgeClass = isCountered
    ? " offer-card--countered"
    : isBoosted
      ? " offer-card--boosted"
      : fanBoost && !isDemo
        ? " offer-card--fanboost"
        : "";
  // Peek toward the side the hint line names (accept = the face slides
  // right, showing the green panel). CSS-only; phones only; never with
  // reduced motion (OfferCard.css).
  const peekClass =
    isPending && nudge && !nudgeDone ? (nudge === "accept" ? " offer-card--peek-right" : " offer-card--peek-left") : "";

  return (
    <>
      <div
        className={`offer-card ${isPending ? "is-pending" : ""}${edgeClass}${peekClass}`}
        data-move-key={moveKey}
        data-move-list={moveList}
        onMouseDown={handleStart}
        onMouseMove={handleMove}
        onMouseUp={handleEnd}
        onMouseLeave={handleEnd}
        onTouchStart={handleStart}
        onTouchMove={handleMove}
        onTouchEnd={handleEnd}
      >
        {/* Solid panels under the card face (founder pick 6c-A, 1.5.1 #6):
            green Accept with the amount, red Decline WITHOUT one (money only
            ever shows on the green side), amber Withdraw keeps the counter
            amount. Absolutely positioned — card heights never move. */}
        {isPending && !declineOnly && (
          <div className="actions-layer actions-left" aria-hidden>
            <div className="action-pill accept" onClick={triggerAccept}>
              <span className="action-amount">{fmt(totalRounded)}</span>
              <span className="action-label">
                <T k="offerCard.accept" tags={{ 0: ACCEPT_ICON }} />
              </span>
            </div>
          </div>
        )}

        {canSwipe && (
          <div
            className={`actions-layer actions-right${isCountered ? " actions-right--withdraw" : ""}`}
            aria-hidden
          >
            {isCountered ? (
              <div className="action-pill withdraw" onClick={triggerWithdraw}>
                <span className="action-amount">{fmt(counterRounded)}</span>
                <span className="action-label">
                  <T k="offerCard.withdraw" tags={{ 0: WITHDRAW_ICON }} />
                </span>
              </div>
            ) : (
              <div className="action-pill decline" onClick={triggerDecline}>
                <span className="action-label">
                  <T k="offerCard.decline" tags={{ 0: DECLINE_ICON }} />
                </span>
              </div>
            )}
          </div>
        )}

        <div
          className="card-inner"
          style={{ transform: `translateX(${offset}px)` }}
          onClick={() => {
            if (offset !== 0) setOffset(0);
          }}
        >
          {/* Inside card-inner so the sticker rides along with the swipe
              (it used to sit on the stationary frame and stayed behind —
              founder report, 1.1(17)). */}
          {isDemo && (
            <span className="demo-chip" aria-label={tr("offerCard.demoRequest")}>
              {tr("offerCard.demo")}
            </span>
          )}
          {isStacked && (
            <button
              type="button"
              className="stack-badge stack-badge--sticker"
              onClick={(e) => {
                e.stopPropagation();
                setShowStackModal(true);
              }}
              aria-label={tr("offerCard.peopleRequestedThisSong", { stackCount })}
            >
              <span className="stack-emoji" aria-hidden>🔥</span>
              <span className="stack-count">×{stackCount}</span>
            </button>
          )}
          {createdAt && (
            <span className="card-timestamp">
              <span className="card-timestamp-elapsed">{formatElapsed(createdAt)}</span>
            </span>
          )}
          {/* The album art is ALWAYS the first thing on the left —
              the old ⚡ + amount column that pushed boosted cards' art to the
              right is gone; the boost now lives in the amount row below, so
              every card's art lines up and the fan message gets the room. */}
          <div className="offer-left">
            {isPlaylist ? (
              /* Setlist sale — no track to link to. With art: the 2×2 cover
                 mosaic (founder 8/25); without: the 🎚️ tile from the
                 approved v4 mock. Same fixed .song-avatar box either way —
                 card heights don't move (request-card contract). */
              trackImages && trackImages.length ? (
                <SetlistArt images={trackImages} className="song-avatar" />
              ) : (
                <div className="song-avatar song-avatar--playlist" aria-hidden="true">
                  🎚️
                </div>
              )
            ) : (
              <a
                href={`https://open.spotify.com/track/${trackId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={avatarUrl}
                  alt={tr("offerCard.song")}
                  className="song-avatar"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).src = "/fallback.png";
                  }}
                />
              </a>
            )}

            <div className="song-details">
              <div className="song-title" title={titleFull}>
                {titleDisplay}
              </div>
              <div className="song-author" title={authorFull}>
                {authorDisplay}
              </div>

              {isPlaylist && !isCountered ? (
                /* Setlist sale — amber 🎚️ PLAYLIST badge in the BOOSTED row
                   slot + the amount in money-green (v4 mock DJ 04). Same
                   pinned-line-height skeleton as BOOSTED / COUNTER SENT so
                   the row height matches every sibling exactly. */
                <div
                  className="song-amount song-amount--boosted song-amount--playlist"
                  title={tr("offerCard.playlistSale", { amount: fmt(totalRounded) })}
                >
                  <span className="boost-label playlist-label">
                    <T k="offerCard.playlist" tags={{ 0: (c) => <span className="boost-label-bolt" aria-hidden>{c}</span> }} />
                  </span>
                  <span className="boost-new playlist-amt">{fmt(totalRounded)}</span>
                </div>
              ) : isCountered ? (
                <div
                  className="song-amount song-amount--countered"
                  title={tr("offerCard.counterOffer", { amount: fmt(totalRounded), amount2: fmt(counterRounded) })}
                >
                  <span className="counter-sent-label">{tr("offerCard.counterSent")}</span>
                  {/* Old price + arrow are decoration — under width pressure
                      they ellipsize away FIRST (plain text span, so the
                      ellipsis behaves; a nested flex group here shrank the
                      label instead). */}
                  <span className="counter-morph-old" aria-hidden>
                    <span className="counter-old">{fmt(totalRounded)}</span>
                    {" →"}
                  </span>
                  <span className="counter-new">{fmt(counterRounded)}</span>
                </div>
              ) : isBoosted && !isDemo ? (
                /* Boosted / Fast Pass: the SAME row shape as the
                   counter ("badge, old → new"), in yellow. One tap opens the
                   boost popup (the old ⚡ column's tap-through lives on). The
                   struck old price only renders when this request really had
                   one (see boostFrom) — a plain Fast Pass is badge + amount. */
                <button
                  type="button"
                  className="song-amount song-amount--boosted"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowPriorityModal(true);
                  }}
                  title={
                    showBoostFrom
                      ? tr("offerCard.boostedTapForDetails", { amount: fmt(boostFrom as number), amount2: fmt(totalRounded) })
                      : tr("offerCard.boostedRequestTapFor", { amount: fmt(totalRounded) })
                  }
                >
                  <span className="boost-label">
                    <T k="offerCard.boosted" tags={{ 0: (c) => <span className="boost-label-bolt" aria-hidden>{c}</span> }} />
                  </span>
                  {showBoostFrom && (
                    <span className="counter-morph-old" aria-hidden>
                      <span className="counter-old">{fmt(boostFrom as number)}</span>
                      {" →"}
                    </span>
                  )}
                  <span className="boost-new">{fmt(totalRounded)}</span>
                </button>
              ) : isDemo ? (
                /* Option B (founder decision): no "$X.00" text row on the
                   demo card — the swipe pill already carries the price and it
                   isn't real money. The practice sentence takes this row's
                   space instead (two-line budget in .demo-subline), so the
                   full sentence fits every phone at uniform card height. */
                <div className="demo-subline">{tr("offerCard.practiceRequestNoReal")}</div>
              ) : fanBoost ? (
                /* ⭐ FAN BOOST (points redemption v1): same pinned
                   skeleton as BOOSTED so card height is untouched — but gold
                   and amount-only. No old→new morph: the fan spent points,
                   not money, and the row must never imply a bigger payout. */
                <div
                  className="song-amount song-amount--boosted song-amount--fanboost"
                  title={tr("offerCard.fanBoostBoostedWith", { amount: fmt(totalRounded) })}
                >
                  <span className="boost-label fanboost-label">
                    <T k="offerCard.fanBoost" tags={{ 0: (c) => <span className="boost-label-bolt" aria-hidden>{c}</span> }} />
                  </span>
                  <span className="boost-new fanboost-amt">{fmt(totalRounded)}</span>
                </div>
              ) : (
                <div className="song-amount" title={`Total: ${fmt(totalRounded)}`}>
                  <span className="song-amount-value">{fmt(totalRounded)}</span>
                </div>
              )}

              {(clientNickname || clientInstagram || comment) && (
                <button
                  type="button"
                  className="song-client"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowClientModal(true);
                  }}
                >
                  <span className="client-icon" aria-hidden>👤</span>
                  {clientNickname && <span className="client-nick">{clientNickname}</span>}
                  {clientInstagram && (
                    <span className="client-ig">
                      {clientInstagramVerified && <InstagramGlyph size={11} className="client-ig-glyph" />}
                      @{clientInstagram}
                    </span>
                  )}
                  {comment && <span className="comment-text">— "{comment}"</span>}
                </button>
              )}
            </div>
          </div>

          {isDesktop && status === "pending" && (
            <div className="offer-actions-desktop" aria-label={tr("offerCard.requestActions")}>
              {!declineOnly && (
                <button
                  type="button"
                  className="card-action-btn card-action-accept"
                  onClick={(e) => {
                    e.stopPropagation();
                    triggerAccept();
                  }}
                >
                  {tr("offerCard.accept2")}
                </button>
              )}
              <button
                type="button"
                className="card-action-btn card-action-decline"
                onClick={(e) => {
                  e.stopPropagation();
                  triggerDecline();
                }}
              >
                {tr("offerCard.decline2")}
              </button>
            </div>
          )}

          {/* Desktop can't swipe (mouse drags are ignored above) — countered
              cards get their single action as an inline button instead. */}
          {isDesktop && isCountered && onWithdrawCounter && (
            <div className="offer-actions-desktop" aria-label={tr("offerCard.counterActions")}>
              <button
                type="button"
                className="card-action-btn card-action-withdraw"
                onClick={(e) => {
                  e.stopPropagation();
                  triggerWithdraw();
                }}
              >
                {tr("offerCard.withdraw2")}
              </button>
            </div>
          )}

          {(status === "accepted" || status === "queued") && (
            <div className="offer-right">
              <button
                className={`play-button${nudgePlay ? " play-button--nudge" : ""}`}
                disabled={playBusy}
                onClick={(e) => {
                  // "Money flies into Balance" (founder decision): a copy
                  // of this pill dives into the Balance tab so talent learn
                  // where the earnings land. Decoration only — the play call
                  // below runs exactly as before. Not for the practice card
                  // (no real money moves there).
                  if (!isDemo) flyMoneyToBalance(e.currentTarget, playLabel);
                  onPlay?.();
                }}
                title={tr("offerCard.playAccepted", { amount: fmt(totalRounded) })}
                aria-label={`Play, ${fmt(totalRounded)}`}
              >
                {playLabel}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* The card lives inside the page's own scroll container (see
          .reqs-scroll). These three tap-throughs are position:fixed, so they
          render straight into <body> — a composited scroller can clip fixed
          descendants on iOS WebKit (STACKING CONTRACT v2), and the fan-message
          tap-through is the request-card contract's §3 escape hatch: it must
          never be trapped inside the scroll box. */}
      {createPortal(
        <>
        {showClientModal && (
          <div
            className="soon-modal-overlay"
            onClick={() => setShowClientModal(false)}
          >
            <div className="soon-modal" onClick={(e) => e.stopPropagation()}>
              <h3>{tr("offerCard.requestDetails")}</h3>
              {clientNickname && (
                <p style={{ marginTop: 4 }}>
                  <strong>{tr("offerCard.name")}</strong> {clientNickname}
                </p>
              )}
              {clientInstagram && (
                <p style={{ marginTop: 4 }}>
                  <strong>{tr("offerCard.instagram")}</strong>{" "}
                  {clientInstagramVerified && <InstagramGlyph size={15} className="client-modal-glyph" />}
                  <a
                    href={`https://instagram.com/${clientInstagram}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#24C961", fontWeight: 600, textDecoration: "none" }}
                  >
                    @{clientInstagram}
                  </a>
                </p>
              )}
              {comment && (
                <p style={{ marginTop: 8, fontStyle: "italic", opacity: 0.9 }}>
                  "{comment}"
                </p>
              )}
              <button onClick={() => setShowClientModal(false)}>{tr("offerCard.close")}</button>
            </div>
          </div>
        )}

        {showStackModal && (
          <div
            className="soon-modal-overlay"
            onClick={() => setShowStackModal(false)}
          >
            <div className="soon-modal" onClick={(e) => e.stopPropagation()}>
              <h3>{tr("offerCard.peopleRequestedThis", { stackCount })}</h3>
              <p>
                {tr("offerCard.theAmountShownIs")}
              </p>
              {contributors.length > 0 && (
                <div style={{ marginTop: 10, fontSize: 13, lineHeight: 1.5 }}>
                  <strong style={{ display: "block", marginBottom: 4, opacity: 0.9 }}>
                    {tr("offerCard.requestedBy")}
                  </strong>
                  {contributors.map((c, i) => {
                    const hasName = !!(c.nickname || c.instagram);
                    return (
                      <div
                        key={i}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 12,
                          opacity: hasName ? 0.9 : 0.7,
                        }}
                      >
                        <span style={{ fontStyle: hasName ? "normal" : "italic" }}>
                          👤{" "}
                          {c.nickname || ""}
                          {c.nickname && c.instagram ? " " : ""}
                          {c.instagram && c.instagramVerified ? <InstagramGlyph size={13} className="client-modal-glyph" /> : null}
                          {c.instagram ? (
                            <a
                              href={`https://instagram.com/${c.instagram}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: "#24C961", textDecoration: "none" }}
                            >
                              @{c.instagram}
                            </a>
                          ) : null}
                          {!hasName ? tr("offerCard.anonymous") : ""}
                        </span>
                        {c.createdAt && (
                          <span style={{ opacity: 0.7, fontSize: 11, whiteSpace: "nowrap" }}>
                            {formatElapsed(c.createdAt)}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              <p style={{ opacity: 0.85, marginTop: 10 }}>
                {tr("offerCard.whenYouAcceptDecline")}
              </p>
              <button onClick={() => setShowStackModal(false)}>{tr("offerCard.gotIt")}</button>
            </div>
          </div>
        )}

        {showPriorityModal && (
          <div
            className="soon-modal-overlay"
            onClick={() => setShowPriorityModal(false)}
          >
            <div className="soon-modal" onClick={(e) => e.stopPropagation()}>
              {/* The tap-through behind the ⚡ BOOSTED row. Shows BOTH the Fast
                  Pass and the boost story when both apply ("no pop-up wins —
                  show both"); amounts are the card's real numbers only — no
                  estimated "original" when the order never had one. */}
              {tierVal === "fast" && (
                <>
                  <h3 style={{ marginBottom: 4 }}>{tr("offerCard.fastPassRequest")}</h3>
                  <p style={{ marginTop: 0 }}>
                    {boostCount >= 1
                      ? tr("offerCard.theFanStartedThis")
                      : tr("offerCard.theFanPaidFast")}
                  </p>
                </>
              )}
              {boostCount >= 1 && (
                <>
                  <h3 style={{ marginTop: tierVal === "fast" ? 16 : 0, marginBottom: 4 }}>
                    {tr("offerCard.thisRequestWasBoosted")}
                  </h3>
                  <p style={{ marginTop: 0 }}>
                    {boostCount > 1 ? tr("offerCard.fanIncreasedBidTimes", { count: boostCount }) : tr("offerCard.fanIncreasedBidOnce")}
                  </p>
                </>
              )}
              <p style={{ opacity: 0.9, marginTop: 12 }}>
                {showBoostFrom && (
                  <>
                    <T k="offerCard.originalAmount" vars={{ amount: fmt(boostFrom as number) }} tags={{ 0: (c) => <strong>{c}</strong> }} />
                    <br />
                  </>
                )}
                {isStacked ? tr("offerCard.combinedTotal") : boostCount >= 1 ? tr("offerCard.currentTotal") : tr("offerCard.fastPassAmount")}:{" "}
                <strong style={{ color: "#fbbf24" }}>{fmt(totalRounded)}</strong>
              </p>
              <button onClick={() => setShowPriorityModal(false)}>{tr("offerCard.ok")}</button>
            </div>
          </div>
        )}
        </>,
        document.body
      )}
    </>
  );
}
